import { useRef, type ReactNode } from "react";

import { useRegisterCommand } from "./hooks";
import { CommandRegistry, CommandRegistryContext } from "./registry";
import type {
  ActionCommand as ActionCommandType,
  CollectionCommand as CollectionCommandType,
  NavigationCommandTarget,
  NavigationCommand as NavigationCommandType,
} from "./types";

/**
 * Declarative command registration. Each component renders nothing — it
 * registers its command into the nearest CommandRegistry (the global one, or a
 * collection's child registry) while mounted, and unregisters on unmount.
 * Conditional rendering (`cond && <NavCommand … />`) is how contextual commands
 * appear and disappear.
 */

type NavCommandProps = Omit<NavigationCommandType, "kind" | "target"> &
  NavigationCommandTarget;

export function NavCommand({ to, params, search, ...meta }: NavCommandProps) {
  useRegisterCommand({
    kind: "navigation",
    ...meta,
    target: { to, params, search },
  });
  return null;
}

type ActionCommandProps = Omit<ActionCommandType, "kind" | "run"> & {
  onRun: ActionCommandType["run"];
};

export function ActionCommand({ onRun, ...meta }: ActionCommandProps) {
  useRegisterCommand({ kind: "action", run: onRun, ...meta });
  return null;
}

type CommandCollectionProps = Omit<
  CollectionCommandType,
  "kind" | "getChildren"
> & {
  children: ReactNode;
};

// A collection owns its own CommandRegistry instance and hands it to
// descendants via context. Child <Command>s therefore register into this
// private registry rather than the global one (useRegisterCommand always
// targets the nearest CommandRegistryContext), and the collection exposes them
// through getChildren — read lazily, only when the palette opens the
// collection. Reusing the same registry class means nesting works at any depth
// for free.
export function CommandCollection({
  children,
  ...meta
}: CommandCollectionProps) {
  const child = useRef<CommandRegistry | undefined>(undefined);
  if (!child.current) child.current = new CommandRegistry();

  useRegisterCommand({
    kind: "collection",
    ...meta,
    getChildren: () => child.current!.getSnapshot(),
  });

  return (
    <CommandRegistryContext.Provider value={child.current}>
      {children}
    </CommandRegistryContext.Provider>
  );
}
